import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listUsers, createUser, updateUser, deleteUser, list } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, ShieldCheck, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

export default function Users() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data = [] } = useQuery({ queryKey: ["users"], queryFn: listUsers });
  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: () => list("drivers") });
  const driverName = (id) => drivers.find((d) => d.id === id)?.driver_name;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ role: "user" });

  const inv = () => qc.invalidateQueries({ queryKey: ["users"] });
  const createMut = useMutation({
    mutationFn: () => createUser(form),
    onSuccess: () => { inv(); setOpen(false); setForm({ role: "user" }); toast.success("User created"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Create failed"),
  });
  const patchMut = useMutation({
    mutationFn: ({ id, body }) => updateUser(id, body),
    onSuccess: () => { inv(); toast.success("Updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });
  const delMut = useMutation({
    mutationFn: (id) => deleteUser(id),
    onSuccess: () => { inv(); toast.success("User removed"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  return (
    <Layout>
      <PageHeader title="Users & Access" subtitle="Create accounts, grant admin, enable or disable access">
        <Button size="sm" data-testid="add-user-btn" onClick={() => { setForm({ role: "user" }); setOpen(true); }} className="gap-2 bg-[#0055FF] hover:bg-[#0044CC]">
          <Plus className="h-4 w-4" /> New User
        </Button>
      </PageHeader>

      <div className="p-8 animate-fade-up">
        <div className="rounded-md border bg-white overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                {["User", "Email", "Role", "Access", ""].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-[0.08em] font-semibold">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium flex items-center gap-2 align-top">
                    {u.role === "admin" ? <ShieldCheck className="h-4 w-4 text-[#0055FF]" /> : <UserIcon className="h-4 w-4 text-neutral-400" />}
                    {u.name || "—"}{u.id === me?.id && <span className="text-xs text-muted-foreground">(you)</span>}
                  </TableCell>
                  <TableCell className="font-mono-data text-[13px] align-top">{u.email}</TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => patchMut.mutate({ id: u.id, body: { role: v } })}>
                      <SelectTrigger className="h-8 w-32" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">Dispatcher</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="driver">Driver</SelectItem>
                      </SelectContent>
                    </Select>
                    {u.role === "driver" && (
                      <div className="text-[11px] text-muted-foreground mt-1">{driverName(u.driver_id) ? `→ ${driverName(u.driver_id)}` : "no driver linked"}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch data-testid={`active-${u.id}`} checked={u.active !== false}
                        onCheckedChange={(v) => patchMut.mutate({ id: u.id, body: { active: v } })} />
                      <span className={`text-xs font-semibold ${u.active !== false ? "text-emerald-600" : "text-red-600"}`}>
                        {u.active !== false ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" data-testid={`delete-user-${u.id}`}
                      disabled={u.id === me?.id} onClick={() => delMut.mutate(u.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">New User</DialogTitle>
            <DialogDescription>Create an account. They can sign in with this email & password.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Full Name</Label>
              <Input data-testid="user-name" className="mt-1.5 h-9" value={form.name || ""} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} /></div>
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Email</Label>
              <Input data-testid="user-email" type="email" className="mt-1.5 h-9" value={form.email || ""} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} /></div>
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Temporary Password (min 8)</Label>
              <Input data-testid="user-password" type="text" className="mt-1.5 h-9 font-mono-data" value={form.password || ""} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} /></div>
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v, driver_id: v === "driver" ? p.driver_id : undefined }))}>
                <SelectTrigger className="mt-1.5 h-9" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="user">Dispatcher</SelectItem><SelectItem value="admin">Admin</SelectItem><SelectItem value="driver">Driver</SelectItem></SelectContent>
              </Select></div>
            {form.role === "driver" && (
              <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Link to Driver Record *</Label>
                <Select value={form.driver_id || ""} onValueChange={(v) => setForm((p) => ({ ...p, driver_id: v }))}>
                  <SelectTrigger className="mt-1.5 h-9" data-testid="user-driver"><SelectValue placeholder="Select driver" /></SelectTrigger>
                  <SelectContent>
                    {drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id} data-testid={`user-driver-opt-${d.id}`}>
                        {d.driver_name} {d.employee_id ? `(#${d.employee_id})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-user-btn" className="bg-[#0055FF] hover:bg-[#0044CC]" disabled={createMut.isPending || (form.role === "driver" && !form.driver_id)} onClick={() => createMut.mutate()}>Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
